import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type MarkAssignmentView } from '../../api';

const kb = (n: number | null) => (n == null ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`);

/** Marking one assignment: read it, give a mark out of 100, pass or fail, say why. */
export function MarkAssignment() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState<MarkAssignmentView | null>(null);
  const [grade, setGrade] = useState('');
  const [passed, setPassed] = useState<'' | 'yes' | 'no'>('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await api.get<MarkAssignmentView>(`/instructor/marking/assignments/${submissionId}`);
      setView(v);
      if (v.submission.gradedAt) {
        setGrade(String(v.submission.grade ?? ''));
        setPassed(v.submission.passed ? 'yes' : 'no');
        setFeedback(v.submission.feedback ?? '');
      }
    } catch (e) { setError((e as ApiError).message); }
  }, [submissionId]);
  useEffect(() => { void load(); }, [load]);

  if (error && !view) return <p className="auth__error">{error}</p>;
  if (!view) return <p className="muted">Loading…</p>;

  const numeric = Number(grade);
  const ready = grade.trim() !== '' && Number.isFinite(numeric) && numeric >= 0 && numeric <= 100 && passed !== '';

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/instructor/marking/assignments/${submissionId}`, {
        grade: numeric, passed: passed === 'yes', feedback: feedback.trim() || undefined,
      });
      setDone(true);
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs">
        <Link to="/marking">To mark</Link> <span aria-hidden>›</span> {view.unit.name}
      </nav>

      <div className="page-head">
        <h1 className="h1">{view.learner.firstName} {view.learner.lastName}</h1>
        <span className="muted">
          {view.course.name} · {view.unit.name} · handed in {new Date(view.submission.submittedAt).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {done && (
        <p className="notice">
          Marked. The trainee can see the mark and your comments now. <Link to="/marking">Back to the queue</Link>
        </p>
      )}
      {error && <p className="auth__error">{error}</p>}

      {view.unit.promptHtml && (
        <section className="panel">
          <h2 className="h2">The brief</h2>
          <div className="prose" dangerouslySetInnerHTML={{ __html: view.unit.promptHtml }} />
        </section>
      )}

      <section className="panel">
        <h2 className="h2">What they handed in</h2>
        {view.submission.text
          ? <p className="submission">{view.submission.text}</p>
          : <p className="muted">No written part.</p>}
        {view.file && (
          <p>
            <a className="btn btn--ghost" href={`/api/media/${view.file.id}`} target="_blank" rel="noreferrer">
              Open {view.file.name ?? 'the attachment'}
            </a>
            <span className="muted small"> {kb(view.file.sizeBytes)}</span>
          </p>
        )}
      </section>

      {view.history.length > 0 && (
        <section className="panel">
          <h2 className="h2">Earlier attempts</h2>
          <ul className="plain">
            {view.history.map((h) => (
              <li key={h.id}>
                {new Date(h.submittedAt).toLocaleDateString()} —{' '}
                {h.grade == null ? 'not marked' : `${h.grade}% ${h.passed ? 'passed' : 'not passed'}`}
                {h.feedback && <span className="muted"> · {h.feedback}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h2 className="h2">{view.submission.gradedAt ? 'Change the mark' : 'Your mark'}</h2>
        <div className="form">
          <label><span>Mark out of 100</span>
            <input type="number" min={0} max={100} inputMode="numeric" value={grade}
              onChange={(e) => setGrade(e.target.value)} /></label>
          <label><span>Outcome</span>
            <select value={passed} onChange={(e) => setPassed(e.target.value as any)}>
              <option value="">Choose…</option>
              <option value="yes">Passed — the unit is complete</option>
              <option value="no">Not passed — they may hand in again</option>
            </select></label>
          <label><span>Comments for the trainee</span>
            <textarea rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)}
              placeholder="What was good, and what to change if they hand in again." /></label>
          <div className="row">
            <button className="btn btn--primary" disabled={busy || !ready} onClick={() => void save()}>
              {busy ? 'Saving…' : view.submission.gradedAt ? 'Save the change' : 'Save mark'}
            </button>
            <button className="btn btn--ghost" onClick={() => navigate('/marking')}>Back</button>
          </div>
        </div>
      </section>
    </>
  );
}
